/**
 * Saying something to an agent: the one implementation, for every box.
 *
 * More than one place offers this, and they are the same intent typed into
 * whichever box was nearest. So they share these two functions rather than
 * copies that drift: a first message that skips the title, or goes to a
 * different model than the composer would have used, or arrives without the
 * skills the person named with `/`, is the kind of inconsistency nobody thinks
 * to report as a bug.
 *
 * THEY HAD ALREADY DRIFTED, which is why this file grew. `WorldShell.startSession`
 * was a second copy that carried skills, attachments, the agent and the project;
 * this one carried a title and a model and dropped the rest on the floor. The
 * shell's version is the one that was right, so its body is here now and the
 * shell calls it. `ThreadView.send` was a third copy of the SEND half, with the
 * same agent-derivation rule written out again.
 *
 * TWO FUNCTIONS, because there are two acts. `sendToSession` says something to a
 * session that exists. `startSessionWithMessage` makes one and then says it.
 * A composer that can address either needs both, and folding them into one call
 * with a nullable id would hide which of the two happened from its own caller.
 *
 * The order matters. `show` runs BEFORE the run is queued so the thread is on
 * screen while the send resolves, and a failure rejects: the caller's composer
 * puts the draft back, and the empty session it landed on is retype-able. A
 * message that looks sent and is not is the worse of the two failures
 * (RFC 0255 §3).
 *
 * WHICH PROJECT IT LANDS IN is the caller's decision, passed in rather than
 * assumed here. Creating in the wrong project does not fail loudly: RFC 0290's
 * whole motivation is that `touchSession` fills in a missing registry entry
 * rather than skipping, so a session created against a project nothing hosts is
 * listed, plausible, and never answered.
 */

import type { Repo } from '@automerge/automerge-repo'
import type { EntryId } from '@mod/core/log'
import {
  type AttachmentRef,
  createSession,
  type MessageSelection,
  type MessageView,
  SessionTaskLog,
} from '@mod/core/model'
import { enqueueRun } from '@mod/core/run'
import { sessionTitleFor } from '../../shell/window/launcherTargets'

/** What the composer chose, beside the words. Every field here is a decision
 *  about THIS message, and dropping one is the drift this file exists against. */
export interface SessionMessage {
  text: string
  /** The model the message is addressed to (RFC 0262). */
  requestedModel: { provider: string; model: string }
  /** World skills named with `/` (RFC 0283 S5), by catalog id. They ride on the
   *  RUN rather than the text, so the host injects their instructions and the
   *  thread still reads as what the person actually said. */
  skills?: string[]
  /** RFC 0288: pointers to attachment documents, created before this call. */
  attachments?: AttachmentRef[]
  /** RFC 0304 3d: shell-captured pixels, hidden from the visible transcript. */
  viewAttachments?: AttachmentRef[]
  /** RFC 0297 §4e: the passage the person was pointing at. Recorded on the
   *  message, so a turn reading the log later knows what "this" meant. */
  selection?: MessageSelection
  /** RFC 0304 3d: the panes visible when this message was sent. This is run
   *  context, not transcript text, so the host can resolve an unnamed "this". */
  view?: MessageView | null
  /** RFC 0302 S3: heads visible in the composer when this post was sent. */
  parents?: EntryId[]
}

/**
 * Which agent answers, when the model names one.
 *
 * A harness id in `provider` addresses the run to that agent, so a
 * multi-harness desktop routes correctly even if `requestedModel` is stripped
 * by an older peer. The two hosted providers are not harnesses and name nobody.
 */
export function agentFor(model: { provider: string }): string | undefined {
  if (model.provider === 'openai' || model.provider === 'anthropic')
    return undefined
  return model.provider || undefined
}

/**
 * Queue a turn on a session that already exists.
 *
 * A queued RUN rather than a plain append: the message is a thing somebody has
 * to answer, and appending it would leave it sitting in a transcript nobody
 * drains. `enqueueRun` is what decides that, and does append only for a
 * CAPTURED session, whose responder is an external process that drains no runs.
 *
 * PASS THE PROJECT where you have it. The daemon hosts the N most recently
 * updated sessions, and `workspaceId` is what marks this one used; a send that
 * omits it can drop the session out of that window, which is the "waiting for a
 * worker" nobody can explain. The old `startSessionWithMessage` omitted it.
 */
export async function sendToSession(
  repo: Repo,
  sessionId: string,
  /** The project whose registry lists this session. Absent where the caller
   *  genuinely does not know, which lets `touchSession` resolve it. */
  projectId: string | undefined,
  msg: SessionMessage,
): Promise<void> {
  await enqueueRun(repo, sessionId, msg.text, {
    ...(projectId ? { projectId } : {}),
    requestedModel: msg.requestedModel,
    agent: agentFor(msg.requestedModel),
    ...(msg.skills?.length ? { skills: msg.skills } : {}),
    ...(msg.attachments?.length ? { attachments: msg.attachments } : {}),
    ...(msg.viewAttachments?.length
      ? { viewAttachments: msg.viewAttachments }
      : {}),
    ...(msg.selection ? { selection: msg.selection } : {}),
    ...(msg.view ? { view: msg.view } : {}),
    ...(msg.parents ? { parents: msg.parents } : {}),
  })
}

/** The task was no longer pending: another session took it first. */
export class TaskTaken extends Error {
  constructor(taskId: string) {
    super(`Task ${taskId} is no longer pending`)
  }
}

export async function startSessionWithMessage(
  repo: Repo,
  /** Which project's registry lists the new session (RFC 0290 S6). */
  projectId: string,
  msg: SessionMessage,
  /** Put the new session on screen. Called before the run is queued, and told
   *  which project holds it so the pane resolves against the right catalog. */
  show: (sessionId: string, projectId: string) => void,
  /** RFC 0304 3c: a pending task the new session takes before its first run,
   *  so that run already has it as its goal. */
  taskId?: string,
): Promise<string> {
  const tasks = taskId
    ? new SessionTaskLog(repo as never, undefined, projectId)
    : null
  // Re-read first: another surface may have taken it since this page drew it.
  const task = taskId ? await tasks?.get(taskId) : null
  if (taskId && task?.status !== 'pending') throw new TaskTaken(taskId)

  // A task's title was written by someone on purpose; the first line typed is
  // a placeholder the agent renames after its first turn (RFC 0311 3b).
  const handle = await createSession(repo, projectId, {
    title: task?.title ?? sessionTitleFor(msg.text),
    titleSetBy: task?.title ? 'you' : 'stub',
  })
  const sessionId = handle.documentId as string
  if (taskId && !(await tasks?.take(taskId, sessionId)))
    throw new TaskTaken(taskId)
  show(sessionId, projectId)
  await sendToSession(repo, sessionId, projectId, msg)
  return sessionId
}
